/**
 * Route-level error boundary.
 *
 * A render crash in one page should not blank the whole app — the drawer, top
 * bar, and every other route must stay usable, because the most likely time
 * something crashes is on a fixture shape nobody anticipated, and the student
 * still needs to get to their other classes.
 *
 * Remounted per route via `key={location.pathname}` in AppShell, so navigating
 * away from a broken page clears the error rather than leaving it stuck.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { tokens } from '@/theme/tokens';

interface Props {
  children: ReactNode;
  /** Rendered instead of the default panel. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
  componentStack: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Logged, not swallowed: an unattended build session that hides its own
    // crashes is worse than useless.
    console.error('[C-GC] Uncaught render error:', error, info.componentStack);
    this.setState({ componentStack: info.componentStack ?? null });
  }

  private reset = (): void => {
    this.setState({ error: null, componentStack: null });
  };

  override render(): ReactNode {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <Box sx={{ p: 3, maxWidth: tokens.size.contentMaxWidth, mx: 'auto' }}>
        <Alert severity="error">
          <AlertTitle>This page hit an error</AlertTitle>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The rest of the app still works — use the navigation to go somewhere else.
          </Typography>

          <Box
            component="pre"
            sx={{
              fontFamily: tokens.fontFamily.mono,
              fontSize: tokens.fontSize.sm,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: tokens.size.courseCardMinHeight,
              overflow: 'auto',
              p: 1.5,
              borderRadius: `${tokens.radius.sm}px`,
              bgcolor: 'action.hover',
              m: 0,
            }}
          >
            {error.name}: {error.message}
            {componentStack ? `\n${componentStack.trim().split('\n').slice(0, 6).join('\n')}` : ''}
          </Box>

          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button size="small" variant="outlined" color="inherit" onClick={this.reset}>
              Try again
            </Button>
            <Button size="small" color="inherit" onClick={() => window.location.reload()}>
              Reload the app
            </Button>
          </Stack>
        </Alert>
      </Box>
    );
  }
}
